import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getDmsPalette } from '../theme/dmsTheme';
import { getManualPalette } from '../components/Manual/manualPalette';

const ThemeToggleContext = createContext({
  toggleTheme: () => {},
  mode: 'dark',
  dmsPalette: null,
  manualPalette: null,
});

export const useThemeToggle = () => useContext(ThemeToggleContext);

const getTheme = (mode) => createTheme({
  palette: {
    mode,
    ...(mode === 'dark' ? {
      primary: { main: '#4fc3f7' },
      secondary: { main: '#ffd54f' },
      background: { default: '#0a1929', paper: '#0d2137' },
      text: { primary: '#e8f4fd', secondary: '#90caf9' },
    } : {
      primary: { main: '#0d47a1' },
      secondary: { main: '#f9a825' },
      background: { default: '#f4f6f9', paper: '#ffffff' },
      text: { primary: '#1a237e', secondary: '#37474f' },
    }),
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCard: { styleOverrides: { root: { borderRadius: 12 } } },
    MuiButton: { styleOverrides: { root: { borderRadius: 8, textTransform: 'none', fontWeight: 600 } } },
    MuiChip: { styleOverrides: { root: { borderRadius: 8 } } },
  },
});

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => localStorage.getItem('dms-theme') || 'dark');

  useEffect(() => { localStorage.setItem('dms-theme', mode); }, [mode]);

  const toggleTheme = () => setMode(prev => prev === 'dark' ? 'light' : 'dark');

  const theme = useMemo(() => getTheme(mode), [mode]);
  const dmsPalette = useMemo(() => getDmsPalette(mode), [mode]);
  const manualPalette = useMemo(() => getManualPalette(mode), [mode]);

  return (
    <ThemeToggleContext.Provider value={{ toggleTheme, mode, dmsPalette, manualPalette }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeToggleContext.Provider>
  );
};
